import type {
  AgentBehavior,
  AiMode,
  DoDItem,
  Effort,
  LastModel,
  ModelDefault,
  ProviderInfo,
  RunCeilings,
} from "@racler/protocol";

/** What the composer sends with a message. Provider → model → effort are dependent choices. */
export interface Selection {
  provider: string;
  model: string;
  effort?: Effort | undefined;
  /** Tool-permission mode of the message. Chat is read-only; Agent performs development work. */
  mode?: AiMode | undefined;
  behavior?: AgentBehavior | undefined;
  strictReview?: boolean | undefined;
  dod?: DoDItem[] | undefined;
  ceilings?: RunCeilings | undefined;
}

/** Providers the user can pick: configured on the machine (credential exists locally). */
export const configuredProviders = (ps: ProviderInfo[]): ProviderInfo[] => ps.filter((p) => p.configured);

export const modelsOf = (ps: ProviderInfo[], providerId: string) =>
  ps.find((p) => p.id === providerId)?.models ?? [];

/** Effort values the selected model really supports (empty = do not show an effort control). */
export const effortsOf = (ps: ProviderInfo[], sel: Selection | null): Effort[] =>
  (sel && modelsOf(ps, sel.provider).find((m) => m.id === sel.model)?.efforts) || [];

export const isValidSelection = (ps: ProviderInfo[], sel: Selection | null): sel is Selection =>
  !!sel && modelsOf(ps, sel.provider).some((m) => m.id === sel.model);

/** Changing the provider picks that provider's first model and drops the effort (it belongs to a model). */
export function withProvider(ps: ProviderInfo[], providerId: string): Selection | null {
  const first = modelsOf(ps, providerId)[0];
  return first ? { provider: providerId, model: first.id } : null;
}

/** Changing the model keeps the effort only if the new model supports it. */
export function withModel(ps: ProviderInfo[], sel: Selection, modelId: string): Selection {
  const next: Selection = { provider: sel.provider, model: modelId };
  const supported = effortsOf(ps, next);
  return sel.effort && supported.includes(sel.effort) ? { ...next, effort: sel.effort } : next;
}

export const withEffort = (sel: Selection, effort: Effort | undefined): Selection => ({
  provider: sel.provider,
  model: sel.model,
  ...(effort ? { effort } : {}),
});

const KEY = "racler.model.";

export interface InitialSelectionOpts {
  storage?: Pick<Storage, "getItem"> | null;
  projectOverride?: ModelDefault | null;
  accountDefault?: ModelDefault | null;
}

/**
 * Precedence:
 * temporary chat choice -> Project override -> account default -> browser stored -> safe fallback.
 * If a referenced credential is unavailable, it does not silently route to another provider.
 */
export function initialSelection(
  ps: ProviderInfo[],
  scope: string,
  lastModel: LastModel | null,
  storageOrOpts?: Pick<Storage, "getItem"> | null | InitialSelectionOpts,
): Selection | null {
  const isStorage = (s: unknown): s is Pick<Storage, "getItem"> =>
    typeof s === "object" && s !== null && "getItem" in s;

  const opts: InitialSelectionOpts = isStorage(storageOrOpts)
    ? { storage: storageOrOpts }
    : (storageOrOpts ?? { storage: safeStorage() });

  const storage = opts.storage ?? safeStorage();
  const usable = configuredProviders(ps);

  // 1. Temporary chat choice
  if (isValidSelection(usable, lastModel)) return lastModel;

  // 2. Project override
  if (opts.projectOverride) {
    const sel: Selection = {
      provider: opts.projectOverride.provider,
      model: opts.projectOverride.model,
      ...(opts.projectOverride.effort ? { effort: opts.projectOverride.effort } : {}),
    };
    if (isValidSelection(usable, sel)) return sel;
    const referencedProvider = ps.find((p) => p.id === opts.projectOverride?.provider);
    if (!referencedProvider || !referencedProvider.configured) {
      return sel; // preserve unavailable selection instead of silently switching
    }
  }

  // 3. Account default
  if (opts.accountDefault) {
    const sel: Selection = {
      provider: opts.accountDefault.provider,
      model: opts.accountDefault.model,
      ...(opts.accountDefault.effort ? { effort: opts.accountDefault.effort } : {}),
    };
    if (isValidSelection(usable, sel)) return sel;
    const referencedProvider = ps.find((p) => p.id === opts.accountDefault?.provider);
    if (!referencedProvider || !referencedProvider.configured) {
      return sel; // preserve unavailable selection instead of silently switching
    }
  }

  // 4. Browser's remembered choice for this scope
  try {
    const raw = storage?.getItem(KEY + scope);
    const saved = raw ? (JSON.parse(raw) as Selection) : null;
    if (isValidSelection(usable, saved)) return saved;
  } catch {
    // corrupt storage is ignored
  }

  // 5. Current safe fallback
  for (const p of usable) if (p.models[0]) return { provider: p.id, model: p.models[0].id };
  return null;
}

export function rememberSelection(scope: string, sel: Selection): void {
  try {
    safeStorage()?.setItem(KEY + scope, JSON.stringify(sel));
  } catch {
    // storage unavailable: the selection still works for this session
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** Enter sends, Shift+Enter inserts a newline, Cmd/Ctrl+Enter also sends. IME composition never sends. */
export function isSendKey(e: {
  key: string;
  shiftKey: boolean;
  isComposing?: boolean;
  nativeEvent?: { isComposing?: boolean };
}): boolean {
  if (e.key !== "Enter" || e.shiftKey) return false;
  return !(e.isComposing ?? e.nativeEvent?.isComposing ?? false);
}

/**
 * The three user-facing modes. Each maps to ONE protocol mode/behavior pair and, through `resolveExecutionRoute`
 * (@racler/protocol), to ONE execution environment:
 *   Chat → Host read-only · Agent Interactive → Host read/write · Agent Autonomous → Worktree read/write.
 * Legacy protocol modes (ask / plan / edit / generic agent) are never offered; they only load via `restoreMode`.
 */
export type UiMode = "chat" | "interactive" | "autonomous";

export const MODES: readonly { id: UiMode; label: string; hint: string; placeholder: string }[] = [
  { id: "chat", label: "Chat (read-only)", hint: "read-only", placeholder: "Ask, analyze and plan without changing the project…" },
  {
    id: "interactive",
    label: "Agent Interactive",
    hint: "works in your project",
    placeholder: "Describe a development task…",
  },
  {
    id: "autonomous",
    label: "Agent Autonomous",
    hint: "isolated Git worktree",
    placeholder: "Describe a development task to run autonomously…",
  },
];

/** Protocol `mode` / `behavior` of a visible mode. */
export function protocolModeOf(ui: UiMode): { mode: AiMode; behavior?: AgentBehavior } {
  if (ui === "chat") return { mode: "chat" };
  return { mode: "agent", behavior: ui };
}

export const isAgentUiMode = (ui: UiMode): boolean => ui !== "chat";

const MODE_KEY = "racler.mode.v3";

/** Maps any stored value (current or legacy) onto a visible mode. Unknown values are Chat (the safe, read-only one). */
export function uiModeFromStored(v: string | null | undefined): UiMode {
  switch (v) {
    case "interactive":
    case "autonomous":
    case "chat":
      return v;
    case "agent": // legacy generic Agent
    case "edit":
      return "interactive";
    default: // ask, plan, unknown, corrupt
      return "chat";
  }
}

export function restoreMode(scope: string, storage: Pick<Storage, "getItem"> | undefined): UiMode {
  try {
    const v =
      storage?.getItem(`${MODE_KEY}:${scope}`) ??
      storage?.getItem(`racler.mode.v2:${scope}`) ??
      storage?.getItem(`racler.mode.v1:${scope}`);
    return uiModeFromStored(v);
  } catch {
    return "chat";
  }
}

export function rememberMode(scope: string, mode: UiMode, storage: Pick<Storage, "setItem"> | undefined): void {
  try {
    storage?.setItem(`${MODE_KEY}:${scope}`, mode);
  } catch {
    // private mode etc.: the mode simply is not remembered
  }
}
